import { Modal } from "@mui/material";
import { cloneDeep, set } from "lodash";
import moment from "moment";
import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import LoaderContainer from "../../../components/reusable-ui/common-loader";
import CtaComponent from "../../../components/reusable-ui/cta";
import { CTA_SIZE, CTA_TYPE } from "../../../data/constants";
import {
  isReasonMandatoryForOutlet,
  OUTLET_STATUSES,
} from "../../../data/outlet-status-constants";
import { isMobileDevice } from "../../../helpers/device-info-helper";
import { parseOutletOnlineStatus } from "../../../helpers/outlet-status-helper";
import {
  getOutletOnlineStatus,
  getOutletsData,
  toggleOutletStatus,
} from "../../../redux/slices";
import OutletToggleForm from "../outlet-toggle-form";
import "./style.scss";

interface Props {
  outletData: any;
  onModalClose: any;
  setApiStateModalData: any;
}

const OutletStatusToggleModal = (props: Props) => {
  const { outletData, onModalClose, setApiStateModalData } = props;
  const [data, setData] = useState<any>({
    active: 1,
    reasonNote: "",
    reason: "",
    endDate: null,
  });
  const [isChangesMade, setIsChangesMade] = useState<boolean>(false);
  const [errorMsgMap, setErrMsgMap] = useState<any>({});
  const [isRefreshing, setIsRefreshing] = useState(true);
  const dispatch = useDispatch();

  useEffect(() => {
    fetchOutletStatus();
  }, []);

  const handleInputChange = (value: any, fieldToUpdate: string) => {
    let dataToUpdate = cloneDeep(data);
    set(dataToUpdate, fieldToUpdate, value);
    setData(dataToUpdate);
    setIsChangesMade(true);
  };

  function checkOnline(status: string | undefined) {
    return OUTLET_STATUSES.ONLINE === status;
  }

  function fetchOutletStatus() {
    let dataToPost = {
      brid: outletData?.brid,
      outletId: outletData?.id,
      type: outletData?.type,
    };
    const callback = (response: Record<string, string>) => {
      setData({
        ...data,
        active: checkOnline(
          parseOutletOnlineStatus(response, outletData?.type, outletData?.webhookSourceType)
        ),
      });
      setIsRefreshing(false);
    };
    dispatch(getOutletOnlineStatus({ dataToPost, callback }));
  }

  function fetchAllOutletsData() {
    const dataToPost = {
      aggregatorType: "Order",
      branches: "all",
      // isNew: true,
    };
    dispatch(getOutletsData({ dataToPost, saveStatus: true }));
  }

  const isReasonMandatory = isReasonMandatoryForOutlet(outletData);

  const updateOutletStatus = () => {
    if (
      isReasonMandatory &&
      !data?.reason &&
      !data?.active
    ) {
      setErrMsgMap({ reason: "Reason is required to turn off." });
      return;
    }
    setErrMsgMap({});
    let dataToPost = {
      brid: outletData?.brid,
      data: {
        active: data?.active,
        endDate: data?.active ? null : data?.endDate,
        reason: `${data?.reason}${
          data?.reasonNote ? ` - ${data?.reasonNote}` : ""
        }`,
      },
      outletId: outletData?.id,
      type: outletData?.type,
    };
    const callback = (success: boolean) => {
      fetchAllOutletsData();
      if (success) {
        setApiStateModalData({
          show: true,
          modalText: "Outlet Status Updated Successfully.",
        });
        onModalClose();
      }
    };
    dispatch(toggleOutletStatus({ dataToPost, callback }));
  };

  return (
    <Modal open={true} onClose={onModalClose}>
      <div
        className={`outlet-status-toggle-modal absolute w-[636px] rounded-[8px]
          overflow-hidden left-1/2 -translate-x-1/2 top-1/2 -translate-y-1/2 bg-white pb-[76px]
          sm:left-0 sm:right-0 sm:bottom-0 sm:top-auto sm:translate-x-0 sm:translate-y-0
          sm:w-full sm:h-[90%] sm:rounded-b-[0]
        `}
      >
        <div
          className={`flex items-center justify-between py-[12px] px-[24px] border-b border-black/[0.15]
            sm:px-[16px]
          `}
        >
          <p className="text-22 font-semibold">
            Outlet Status - {outletData?.channelDefName}
          </p>
          <div
            className="rista-sprite ic-cross-black"
            onClick={onModalClose}
          ></div>
        </div>
        <div className="px-[24px] mt-[20px] sm:px-[16px]">
          {isRefreshing ? (
            <LoaderContainer />
          ) : (
            <OutletToggleForm
              handleInputChange={handleInputChange}
              data={data}
              errorMsgMap={errorMsgMap}
              isChangesMade={isChangesMade}
              type={outletData?.type}
              isReasonMandatory={isReasonMandatory}
            />
          )}
        </div>
        <div
          className={`absolute bottom-[0px] w-full z-1 right-0 bg-white h-[70px]
            px-24 flex justify-end border-t border-[#EBEBEB] items-center overflow-hidden
            sm:w-full sm:px-[16px]
          `}
        >
          <CtaComponent
            ctaText={"Discard"}
            ctaType={CTA_TYPE.secondary_cta}
            ctaSize={isMobileDevice() ? CTA_SIZE.small : CTA_SIZE.medium}
            onCtaClick={onModalClose}
            customClass={"mr-10 sm:mr-[8px]"}
          ></CtaComponent>
          <CtaComponent
            ctaText={"Update Outlet Status"}
            ctaType={CTA_TYPE.active_cta}
            ctaSize={isMobileDevice() ? CTA_SIZE.small : CTA_SIZE.medium}
            onCtaClick={updateOutletStatus}
            customClass={"ml-10 sm:ml-[8px]"}
          ></CtaComponent>
        </div>
      </div>
    </Modal>
  );
};

export default OutletStatusToggleModal;
